import {
  Typography,
  Box,
  Stack,
  Link,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { IconHelpCircle, IconVideo, IconChevronDown } from "@tabler/icons-react";

function QA(props) {
  const { question, answer } = props;
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: "1px solid var(--neutral-200)",
        borderRadius: "12px !important",
        overflow: "hidden",
        mb: 1.5,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
        "&:before": { display: "none" },
        "&.Mui-expanded": {
          margin: 0,
          marginBottom: 1.5,
          boxShadow: "0 4px 20px rgba(159, 37, 246, 0.12)",
          borderColor: "var(--secondary-200)",
        },
        "&:hover": {
          borderColor: "var(--neutral-300)",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "8px",
              backgroundColor: "var(--secondary-50)",
              color: "var(--secondary-600)",
              transition: "transform 0.3s ease, background 0.2s ease",
            }}
          >
            <IconChevronDown size={18} stroke={2.5} />
          </Box>
        }
        aria-controls="panel-content"
        id="panel-header"
        className="accordion-summary"
        sx={{
          position: "relative",
          minHeight: 56,
          paddingLeft: 2.5,
          paddingRight: 2,
          backgroundColor: "var(--neutral-50)",
          transition: "background 0.25s ease",
          "&:hover": {
            backgroundColor: "rgba(159, 37, 246, 0.04)",
          },
          "& .MuiAccordionSummary-content": {
            my: 1.75,
            marginRight: 1.5,
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "var(--main-text-color)",
            pr: 1,
            fontSize: "1rem",
            letterSpacing: "0.01em",
          }}
        >
          {question}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          pt: 0,
          pb: 2.5,
          px: 2.5,
          backgroundColor: "rgba(250, 250, 254, 0.8)",
          borderTop: "1px solid var(--neutral-100)",
        }}
      >
        <Box sx={{ pl: 0.5 }}>
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: "var(--body-text-color-600)",
              lineHeight: 1.75,
              "& br": { display: "block", content: '""', marginTop: 1 },
            }}
          >
            {answer}
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

const TUTORIALS = [
  { title: "How To Discover Creators and Make a List", embedId: "qf2B7q4P6hg" },
  { title: "How To Launch a Campaign", embedId: "qHDegOGEltA" },
  { title: "Guide To Partnering with Creators", embedId: "X3w0Ru3OBtk" },
  { title: "How To Boost Campaign Engagement", embedId: "5K-DQFjeRGc" },
  { title: "How To Launch a Private Campaign with Select Creators", embedId: "vQoNyxFhoaI" },
];

export default function FrequentlyAskedQuestions() {
  return (
    <Box sx={{ padding: "3%", maxWidth: 900, mx: "auto" }}>
      <Stack spacing={4}>
        {/* FAQ Section */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: "linear-gradient(135deg, rgba(159,37,246,0.12), rgba(92,164,251,0.08))",
              }}
            >
              <IconHelpCircle size={22} color="var(--secondary-500)" />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "var(--main-text-color)",
                letterSpacing: "0.2px",
              }}
            >
              Frequently Asked Questions
            </Typography>
          </Stack>

              <QA
                question="How to open your campaign for creator applications?"
                answer={
                  <>
                    Set your campaign as “public” by clicking the button on the top
                 of your campaigns page. You can always make your campaign
                private again and update your campaign details at any point. Naturally, the longer your campaign is open the more
                applications you will receive. 
                <br/>
                If you want to increase applications, try outreaching to creators to invite them to apply.
                  </>
                }
              />

              <QA
                question="How to discover more creators?"
                answer={
                  <>
                    Visit your discovery tab and discover thousands of creators. Filter to find the right creators for your campaign.
                  </>
                }
              />

              <QA
                question="How do I modify my creator marketing brief?"
                answer={
                  <>
                  Download the brief, and convert it to a Word doc to modify. Then
                send your custom brief to creators by uploading it in your
                creator workstream section.
                  </>
                }
              />

<QA
                question="How can I increase my campaign engagement?"
                answer={
                  <>
                  1. Start outreaching to creators and invite them to join your campaign.
                  <br/>
                  2. Ensure your campaign details, deliverables and compensation is clear. Paid campaigns typically have higher engagement
                  </>
                }
              />

              <QA
                question="How do I partner with a creator, and what happens next?"
                answer={
                  <>
                  Select your creator match from your applications tab. On the creator
                application, select “confirm partnership”. This partnership will unlock your Creator
                Workstream Section. Visit this worktream tab and the creator will be
                waiting for your first message. Say “Hi” and share any
                additional campaign details to get started!
                  </>
                }
              />

              <QA
                question="What are the standard rates for influencer marketing?"
                answer={
                  <>
                    Visit your Analytics Dashboard to see the most recent data
                    on paid partnership rates.
                    <br />
                    Please Note: these rates can vary based on the influencer
                    and their set rates. Pricing can also change based on
                    factors such as, but not limited to, usage rights,
                    exclusivity, link in bio, campaign length, and timeline
                    rush.
                  </>
                }
              />

              <QA
                question="What if I want only to launch a gifted / merchandise campaign?"
                answer={`When launching your campaign, select "merchandise campaign." You can also outline the details of your merchandise campaign in the "details" section specifying what type of merchandise and the merchandise value you will provide in this campaign.`}
              />
              <QA
                question="How do I launch an influencer marketing campaign?"
                answer={
                  <>
                    Select the "launch campaign" button at the top of your
                    dashboard and fill out the form with all the details of the
                    campaign you would like to launch. Once your campaign is
                    launched you can go to your campaign page and select "Make
                    Public." This will make your campaign public to creators.
                    <br />
                    <br />
                    You can keep your campaign public for as long as you would
                    like, and wait for creators to show interest in your
                    campaign. The creators that are interested in partnering
                    with you will be shown in your "Featured for You" creators
                    section and under "More creators that love your brand" You
                    can then select the creators you would like to partner with
                  </>
                }
              />

              <QA
                question="What is the difference between Featured Creators and More Creators who love my brand?"
                answer={
                  <>
                    Featured creators for you are ones that according to your
                    brand campaign are a great fit and match your criteria. Or
                    have submitted a custom application to stand out because
                    they really love your brand!
                    <br />
                    <br />
                    More creators who love your brand have directly applied to
                    your campaign, however, may not exactly match all your
                    criteria. The creators may still be a great fit for your
                    campaign, so you could find your perfect influencer match in
                    this pool of creators!
                  </>
                }
              />

              <QA
                question="Do I need a marketing brief for an influencer marketing campaign?"
                answer={
                  <>
                    Having a marketing brief is completely up to you! Some
                    brands prefer having a brief for creators to work with.
                    However, other brands simply prefer to share the campaign
                    guidelines and details via communications with the creator.
                    <br />
                    <br />
                    At Gigl, we help you skip the paperwork! You can generate a
                    custom marketing brief to send creators with just one click!
                    Select the "Generate Brief" button at the top right corner
                    of your campaign page.
                  </>
                }
              />

              <QA
                question="What if I already have specific creators that I am working with for this campaign?"
                answer={
                  <>
                    When launching your campaign you can select “Private
                    Campaign” and outline the creator codes of the specific
                    creators you are working with. Each creator on Gigl has a
                    unique creator code. Ask them to provide you with their
                    specific creator code and input that in the “creator code
                    field.” Once you launch that campaign you will automatically
                    be partnered with those creators.
                  </>
                }
              />

              <QA
                question="How do I cancel my monthly susbscription?"
                answer={
                  <>
                    Email us at support@giglgroup.com saying 1. Your account
                    email, 2. You would like to cancel your subscription. Our
                    team will cancel your subscription moving forward. We thank
                    you for using Gigl.
                  </>
                }
              />
        </Box>

        {/* Tutorials Section */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: "linear-gradient(135deg, rgba(159,37,246,0.12), rgba(92,164,251,0.08))",
              }}
            >
              <IconVideo size={22} color="var(--secondary-500)" />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "var(--main-text-color)",
                letterSpacing: "0.2px",
              }}
            >
              Gigl Tutorials and How-To Guides
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: "var(--body-text-color-600)", mb: 3, lineHeight: 1.6 }}>
            Watch customized guides on how to use the Gigl platform to drive successful campaigns.
          </Typography>
          <Stack spacing={3}>
            {TUTORIALS.map((item, index) => (
              <Box
                key={item.embedId}
                sx={{
                  background: "#fff",
                  borderRadius: "12px",
                  border: "1px solid var(--neutral-200)",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "var(--main-text-color)",
                    p: 2,
                    pb: 1,
                  }}
                >
                  {index + 1}. {item.title}
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    paddingBottom: "56.25%",
                    height: 0,
                    overflow: "hidden",
                    "& iframe": {
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: 0,
                    },
                  }}
                >
                  <iframe
                    src={`https://www.youtube.com/embed/${item.embedId}`}
                    title={item.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Contact */}
        <Box
          sx={{
            py: 4,
            px: 2,
            textAlign: "center",
            borderTop: "1px solid var(--neutral-100)",
          }}
        >
          <Typography variant="body2" sx={{ color: "var(--body-text-color-600)", mb: 0.5 }}>
            Have more questions?
          </Typography>
          <Link
            href="mailto:support@giglgroup.com"
            sx={{
              fontWeight: 600,
              color: "var(--secondary-600)",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            support@giglgroup.com
          </Link>
        </Box>
      </Stack>
    </Box>
  );
}
